// TemplatesPage — list + create + open builder + client-library send/manage
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Loader2, X, Save, ListChecks, Pencil, Sparkles, Send, Eye, Building2, CheckSquare, FilePlus2, ClipboardCheck, Trash2, MoreVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSortableRows, SortableTh } from "@/lib/sortable";
import Pill from "@/components/Pill";
import PublishWizard from "@/components/issues/PublishWizard";
import AllocateTemplateWizard from "@/components/documents/AllocateTemplateWizard";
import FormPreviewDialog from "@/components/forms/FormPreviewDialog";

const CATEGORIES = [
    { value: "inspection", label: "Inspection" },
    { value: "audit", label: "Audit" },
    { value: "fra", label: "Fire Risk Assessment" },
    { value: "bsc", label: "Building Safety Case" },
    { value: "cdm", label: "CDM" },
];

const STATUS_TONE = { draft: "amber", pending_approval: "blue", rejected: "red", published: "green", archived: "neutral" };
const STATUS_LABEL = { pending_approval: "pending approval", rejected: "rejected" };
const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);

export default function TemplatesPage() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(false);
    const [issueFor, setIssueFor] = useState(null);
    const [allocateFor, setAllocateFor] = useState(null);
    const [previewFor, setPreviewFor] = useState(null);
    const [clients, setClients] = useState([]);
    const [libraryClientId, setLibraryClientId] = useState("");   // internal-only: browse a specific client's own library
    const [clonesSummary, setClonesSummary] = useState({});       // masters view only: master.id -> [{client_name, completed_by}]
    const [selected, setSelected] = useState(new Set());
    const [sendDialogIds, setSendDialogIds] = useState(null);
    const [pending, setPending] = useState([]);        // Prompt 2 — PR approval queue
    const [reviewBusy, setReviewBusy] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null); // row id whose consolidated Actions menu is open
    const { user } = useAuth();
    const navigate = useNavigate();
    const isInternal = user && INTERNAL_ROLES.has(user.role);
    const isClientAdmin = user?.role === "client_admin";
    const isSiteManager = user?.role === "site_manager";
    const ownClientId = user?.client_id || (user?.client_ids || [])[0] || null;
    const canManageOwn = isInternal || ((isClientAdmin || isSiteManager) && user?.can_manage_forms !== false);   // can create/AI-import into their own library
    const isMastersView = isInternal && !libraryClientId;

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = isInternal && libraryClientId ? { client_id: libraryClientId } : {};
            const { data } = await api.get("/forms/templates", { params });
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load templates");
        }
        setLoading(false);
    }, [isInternal, libraryClientId]);

    useEffect(() => { refresh(); setSelected(new Set()); }, [refresh]);

    useEffect(() => {
        function onDocClick(e) { if (!e.target.closest("[data-row-action]")) setOpenMenuId(null); }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    useEffect(() => {
        if (isInternal) api.get("/clients").then((r) => setClients(r.data)).catch(() => { });
    }, [isInternal]);

    const refreshPending = useCallback(() => {
        if (!isInternal) { setPending([]); return; }
        api.get("/forms/templates/pending").then((r) => setPending(r.data)).catch(() => setPending([]));
    }, [isInternal]);
    useEffect(() => { refreshPending(); }, [refreshPending]);

    async function reviewTemplate(t, action) {
        setReviewBusy(t.id);
        try {
            if (action === "approve") {
                await api.post(`/forms/templates/${t.id}/approve`);
                toast.success(`"${t.name}" approved & published`);
                if (t.client_id) setLibraryClientId(t.client_id);   // jump to the client's library so it's immediately visible
            } else {
                const note = window.prompt("Why is this being rejected? The proposer will see this note.");
                if (note == null || !note.trim()) { setReviewBusy(null); return; }
                await api.post(`/forms/templates/${t.id}/reject`, { note: note.trim() });
                toast.success(`"${t.name}" rejected`);
            }
            refreshPending();
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Review action failed");
        }
        setReviewBusy(null);
    }

    useEffect(() => {
        if (isMastersView) {
            api.get("/forms/templates/clones-summary").then((r) => setClonesSummary(r.data)).catch(() => { });
        } else {
            setClonesSummary({});
        }
    }, [isMastersView, rows]);

    const filtered = rows.filter((t) => {
        if (statusFilter !== "All" && t.status !== statusFilter) return false;
        if (q && !`${t.name} ${t.code} ${t.category}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    const accessors = useMemo(() => ({
        name: (t) => t.name || "",
        code: (t) => t.code || "",
        category: (t) => t.category || "",
        sections: (t) => (t.sections || []).length,
        version: (t) => t.version || 0,
        status: (t) => t.status || "",
    }), []);
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(filtered, { accessors });

    const clientName = (cid) => clients.find((c) => c.id === cid)?.name;
    const canEditRow = (t) => isInternal
        || (isClientAdmin && t.client_id === ownClientId && user?.can_manage_forms !== false)
        || (isSiteManager && t.created_by === user?.id && ["pending_approval", "rejected", "draft"].includes(t.status) && user?.can_manage_forms !== false);

    const toggleSelected = (id) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    async function setCompletedBy(t, value) {
        try {
            const { data } = await api.patch(`/forms/templates/${t.id}`, { completed_by: value });
            setRows((rs) => rs.map((r) => (r.id === t.id ? data : r)));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
        }
    }

    async function deleteTemplate(t) {
        if (!window.confirm(`Delete template "${t.name}"? Existing reports already generated from it are unaffected, but it will no longer be usable to start new ones.`)) return;
        try {
            await api.delete(`/forms/templates/${t.id}`);
            toast.success(`"${t.name}" deleted`);
            setRows((rs) => rs.filter((r) => r.id !== t.id));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not delete template");
        }
    }

    return (
        <div data-testid="templates-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                        style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Documents & Forms
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Form Templates</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {rows.length} templates · {rows.filter((t) => t.status === "published").length} published
                        {libraryClientId && ` · viewing ${clientName(libraryClientId) || "client"}'s library`}
                    </p>
                </div>
                {canManageOwn && (
                    <div className="flex items-center gap-2">
                        <button data-testid="template-ai-import-btn" onClick={() => navigate("/app/templates/ai-import")}
                            className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white bg-ai-grad">
                            <Sparkles size={15} /> Import with Beyond AI
                        </button>
                        <button data-testid="template-new-btn" onClick={() => setDialog(true)}
                            className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                            style={{ background: "var(--brand-accent, #C75A1B)" }}>
                            <Plus size={15} /> {isSiteManager ? "Propose a template" : "New template"}
                        </button>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9A968F]" />
                    <input data-testid="template-search" placeholder="Search templates…"
                        value={q} onChange={(e) => setQ(e.target.value)}
                        className="h-9 pl-8 pr-3 rounded-lg border border-[#E5E0DA] bg-white text-sm w-72 focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]" />
                </div>
                <select data-testid="template-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    {["All", "draft", "pending_approval", "rejected", "published", "archived"].map((s) => (
                        <option key={s} value={s}>{s === "All" ? "All statuses" : (STATUS_LABEL[s] || s)}</option>
                    ))}
                </select>
                {isInternal && (
                    <div className="flex items-center gap-1.5 ml-auto">
                        <Building2 size={13} className="text-[#9A968F]" />
                        <select data-testid="template-library-select" value={libraryClientId}
                            onChange={(e) => setLibraryClientId(e.target.value)}
                            className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="">Priority Risk masters</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                )}
            </div>

            {isInternal && pending.length > 0 && (
                <div data-testid="templates-approval-queue" className="rounded-xl border overflow-hidden" style={{ borderColor: "#CBD9EC", background: "#F3F7FC" }}>
                    <div className="px-4 py-2.5 border-b flex items-center gap-2" style={{ borderColor: "#DCE7F3" }}>
                        <ClipboardCheck size={15} className="text-[#2A5DB0]" />
                        <span className="font-semibold text-[13px] text-[#1F1F1F]">Awaiting approval</span>
                        <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-[#2A5DB0] text-white">{pending.length}</span>
                        <span className="text-[11.5px] text-[#6F6A64]">Site-manager proposals — approve to publish for all of the client's site managers</span>
                    </div>
                    <div className="divide-y" style={{ borderColor: "#E4ECF6" }}>
                        {pending.map((t) => (
                            <div key={t.id} data-testid={`pending-template-${t.id}`} className="flex items-center gap-3 px-4 py-2.5">
                                <button onClick={() => navigate(`/app/templates/${t.id}`)} className="min-w-0 flex-1 text-left group">
                                    <div className="font-semibold text-[13px] text-[#1F1F1F] truncate group-hover:underline">{t.name}</div>
                                    <div className="text-[11.5px] text-[#6F6A64] truncate">
                                        {t.client_name} · {t.code} · proposed by {t.submitted_by_name || "site manager"}
                                    </div>
                                </button>
                                <button data-testid={`pending-preview-${t.id}`} onClick={() => setPreviewFor(t)}
                                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F]"><Eye size={13} /> Preview</button>
                                <button data-testid={`pending-reject-${t.id}`} disabled={reviewBusy === t.id} onClick={() => reviewTemplate(t, "reject")}
                                    className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-lg border bg-white text-[#9B2C1E] hover:bg-[#FBEBE8] disabled:opacity-60" style={{ borderColor: "#F1C9C2" }}>Reject</button>
                                <button data-testid={`pending-approve-${t.id}`} disabled={reviewBusy === t.id} onClick={() => reviewTemplate(t, "approve")}
                                    className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-lg text-white disabled:opacity-60" style={{ background: "#3F7D3A" }}>
                                    {reviewBusy === t.id ? <Loader2 size={12} className="animate-spin" /> : <CheckSquare size={12} />} Approve
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {selected.size > 0 && isMastersView && (
                <div data-testid="templates-bulk-bar" className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-[#F5F3F0] border border-[#E5E0DA]">
                    <span className="text-[13px] font-semibold text-[#1F1F1F]">{selected.size} selected</span>
                    <button data-testid="templates-bulk-send-btn" onClick={() => setSendDialogIds([...selected])}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Send size={12} /> Send to Client templates
                    </button>
                    <button onClick={() => setSelected(new Set())} className="text-[12.5px] text-[#6F6A64] hover:text-[#1F1F1F]">Clear</button>
                </div>
            )}

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F3F0] text-left text-[11px] uppercase tracking-wider text-[#6F6A64] border-b border-[#E5E0DA]">
                        <tr>
                            {isMastersView && <th className="w-9 px-3 py-3"></th>}
                            <SortableTh myKey="name" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="templates-th-name" className="group">Template</SortableTh>
                            <SortableTh myKey="code" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="templates-th-code" className="group">Code</SortableTh>
                            <SortableTh myKey="category" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="templates-th-category" className="group">Category</SortableTh>
                            <SortableTh myKey="version" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="templates-th-version" className="group">Version</SortableTh>
                            <SortableTh myKey="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="templates-th-status" className="group">Status</SortableTh>
                            <th className="px-4 py-3 font-semibold">Completed by</th>
                            {isMastersView && <th className="px-4 py-3 font-semibold">Sent to</th>}
                            <th className="px-4 py-3 font-semibold w-24"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={9} className="text-center py-10 text-[#9A968F]">
                                <Loader2 size={16} className="inline animate-spin mr-2" />Loading templates…
                            </td></tr>
                        ) : sorted.length === 0 ? (
                            <tr><td colSpan={9} className="text-center py-10 text-[#9A968F]">No templates match this filter.</td></tr>
                        ) : sorted.map((t) => (
                            <tr key={t.id} data-testid={`template-row-${t.id}`}
                                className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0] cursor-pointer"
                                onClick={(e) => { if (e.target.closest("[data-row-action]")) return; navigate(`/app/templates/${t.id}`); }}>
                                {isMastersView && (
                                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" data-testid={`template-select-${t.id}`}
                                            checked={selected.has(t.id)} onChange={() => toggleSelected(t.id)} />
                                    </td>
                                )}
                                <td className="px-4 py-3">
                                    <div className="font-semibold text-[#1F1F1F]">{t.name}</div>
                                    {t.description && <div className="text-[12px] text-[#6F6A64] truncate max-w-md">{t.description}</div>}
                                    {t.client_id && !libraryClientId && (
                                        <div className="text-[11px] text-[#9A968F] mt-0.5">
                                            <ListChecks size={10} className="inline mr-1" />{clientName(t.client_id) || "Client template"}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-3 font-mono text-[12px] text-[#6F6A64]">{t.code}</td>
                                <td className="px-4 py-3 text-[#6F6A64] capitalize">{t.category}</td>
                                <td className="px-4 py-3 text-[#6F6A64]">v{t.version}</td>
                                <td className="px-4 py-3"><Pill tone={STATUS_TONE[t.status] || "neutral"}>{STATUS_LABEL[t.status] || t.status}</Pill></td>
                                <td className="px-4 py-3" onClick={(e) => isInternal && e.stopPropagation()}>
                                    <div className="flex items-center gap-1.5">
                                        {isInternal ? (
                                            <select data-testid={`template-completed-by-${t.id}`} value={t.completed_by || "pr"}
                                                onChange={(e) => setCompletedBy(t, e.target.value)}
                                                className="h-7 px-2 rounded-md border border-[#E5E0DA] bg-white text-[12px]">
                                                <option value="pr">PR staff</option>
                                                <option value="client">Client</option>
                                            </select>
                                        ) : (
                                            <Pill tone={t.completed_by === "client" ? "blue" : "neutral"}>
                                                {t.completed_by === "client" ? "Client" : "PR staff"}
                                            </Pill>
                                        )}
                                        {t.pr_approval_required && (
                                            <span data-testid={`template-pr-approval-pill-${t.id}`}
                                                className="text-[10.5px] px-1.5 py-0.5 rounded-full font-semibold"
                                                title="Priority Risk must approve this report after it is submitted"
                                                style={{ background: "#FBEFE3", color: "#9F4212" }}>
                                                PR approval
                                            </span>
                                        )}
                                    </div>
                                </td>
                                {isMastersView && (
                                    <td className="px-4 py-3">
                                        {(clonesSummary[t.id] || []).length === 0 ? (
                                            <span className="text-[12px] text-[#9A968F]">—</span>
                                        ) : (
                                            <div className="flex flex-wrap gap-1">
                                                {(clonesSummary[t.id] || []).map((c) => (
                                                    <span key={c.template_id} className="text-[11px] px-1.5 py-0.5 rounded-full bg-[#EFEAE3] text-[#6F6A64]">
                                                        {c.client_name}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                )}
                                <td className="px-4 py-3 text-right relative" data-row-action>
                                    <button data-testid={`template-actions-trigger-${t.id}`}
                                        onClick={() => setOpenMenuId(openMenuId === t.id ? null : t.id)}
                                        className="w-8 h-8 rounded-lg grid place-items-center hover:bg-[#F0EDE8] ml-auto"
                                        aria-label="Template actions">
                                        <MoreVertical size={16} style={{ color: "#6F6A64" }} />
                                    </button>
                                    {openMenuId === t.id && (
                                        <div data-testid={`template-actions-menu-${t.id}`}
                                            className="absolute right-3 top-[100%] mt-1 bg-white border rounded-lg shadow-brand min-w-[210px] py-1 z-30"
                                            style={{ borderColor: "#E5E0DA" }}>
                                            <button data-testid={`template-preview-${t.id}`}
                                                onClick={() => { setPreviewFor(t); setOpenMenuId(null); }}
                                                className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] text-[#1F1F1F] hover:bg-[#F6F4F0]">
                                                <Eye size={13} /> Preview
                                            </button>
                                            {isInternal && t.status === "published" && !t.client_id && (
                                                <button data-testid={`template-allocate-${t.id}`}
                                                    onClick={() => { setAllocateFor(t); setOpenMenuId(null); }}
                                                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] text-[#2E6B57] hover:bg-[#F6F4F0]">
                                                    <FilePlus2 size={13} /> Allocate as template
                                                </button>
                                            )}
                                            {isInternal && t.status === "published" && !t.client_id && (
                                                <button data-testid={`template-issue-${t.id}`}
                                                    onClick={() => { setIssueFor(t); setOpenMenuId(null); }}
                                                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] text-[#2A5DB0] hover:bg-[#F6F4F0]">
                                                    <Send size={13} /> Issue to recipients
                                                </button>
                                            )}
                                            {canEditRow(t) && (
                                                <button data-testid={`template-edit-${t.id}`}
                                                    onClick={() => { navigate(`/app/templates/${t.id}`); setOpenMenuId(null); }}
                                                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-[#F6F4F0]"
                                                    style={{ color: "var(--brand-accent, #C75A1B)" }}>
                                                    <Pencil size={13} /> Edit
                                                </button>
                                            )}
                                            {isInternal && (
                                                <button data-testid={`template-delete-${t.id}`}
                                                    onClick={() => { setOpenMenuId(null); deleteTemplate(t); }}
                                                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] text-[#9B2C1E] hover:bg-[#FBEBE8]">
                                                    <Trash2 size={13} /> Delete
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {dialog && (
                <NewTemplateDialog onClose={() => setDialog(false)}
                    isInternal={isInternal} clients={clients} defaultClientId={libraryClientId}
                    onCreated={(t) => { setDialog(false); refresh(); navigate(`/app/templates/${t.id}`); }} />
            )}
            {issueFor && <PublishWizard source={{ id: issueFor.id, name: issueFor.name, completion_mode: "form" }} user={user}
                onClose={() => setIssueFor(null)} onIssued={() => { }} />}
            {allocateFor && <AllocateTemplateWizard source={{ id: allocateFor.id, name: allocateFor.name }} user={user} clients={clients}
                onClose={() => setAllocateFor(null)} onAllocated={() => { }} />}
            {previewFor && <FormPreviewDialog template={previewFor} onClose={() => setPreviewFor(null)} />}
            {sendDialogIds && (
                <SendToClientsDialog templateIds={sendDialogIds} clients={clients}
                    onClose={() => setSendDialogIds(null)}
                    onSent={() => { setSendDialogIds(null); setSelected(new Set()); refresh(); }} />
            )}
        </div>
    );
}

function NewTemplateDialog({ onClose, onCreated, isInternal, clients, defaultClientId }) {
    const [form, setForm] = useState({
        code: "", name: "", category: "inspection", description: "", requires_signature: true,
        client_id: defaultClientId || "", completed_by: isInternal ? (defaultClientId ? "client" : "pr") : "client",
    });
    const [saving, setSaving] = useState(false);

    async function submit() {
        if (!form.code.trim() || !form.name.trim()) {
            toast.error("Code and Name are required");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/forms/templates", {
                ...form,
                client_id: form.client_id || null,
                sections: [{ id: `s${Date.now()}`, title: "Section 1", order: 0, fields: [] }],
                status: "draft",
            });
            toast.success("Template created — start adding sections");
            onCreated(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create template");
        }
        setSaving(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="new-template-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">New form template</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Code *">
                            <input data-testid="new-template-code" value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                placeholder="e.g. FD-Q1" className={inputCls} />
                        </Field>
                        <Field label="Category">
                            <select data-testid="new-template-category" value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                className={inputCls}>
                                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label="Name *">
                        <input data-testid="new-template-name" value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Fire Door Quarterly Inspection" className={inputCls} />
                    </Field>
                    <Field label="Description">
                        <textarea rows={3} value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className={inputCls + " resize-none"} />
                    </Field>
                    {isInternal && (
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="For">
                                <select data-testid="new-template-client" value={form.client_id}
                                    onChange={(e) => setForm({ ...form, client_id: e.target.value, completed_by: e.target.value ? "client" : "pr" })}
                                    className={inputCls}>
                                    <option value="">Global master (all clients)</option>
                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </Field>
                            <Field label="Completed by">
                                <select data-testid="new-template-completed-by" value={form.completed_by}
                                    onChange={(e) => setForm({ ...form, completed_by: e.target.value })}
                                    className={inputCls}>
                                    <option value="pr">PR staff</option>
                                    <option value="client">Client</option>
                                </select>
                            </Field>
                        </div>
                    )}
                    <label className="flex items-center gap-2 text-sm text-[#1F1F1F]">
                        <input type="checkbox" checked={form.requires_signature}
                            onChange={(e) => setForm({ ...form, requires_signature: e.target.checked })} />
                        Require signature on submit
                    </label>
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="new-template-save" onClick={submit} disabled={saving}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create
                    </button>
                </div>
            </div>
        </div>
    );
}

function SendToClientsDialog({ templateIds, clients, onClose, onSent }) {
    const [picked, setPicked] = useState(new Set());
    const [completedBy, setCompletedBy] = useState("client");
    const [busy, setBusy] = useState(false);

    const toggle = (id) => setPicked((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    async function submit() {
        if (picked.size === 0) return toast.error("Pick at least one client");
        setBusy(true);
        try {
            const { data } = await api.post("/forms/templates/bulk-send", {
                template_ids: templateIds, client_ids: [...picked], completed_by: completedBy,
            });
            toast.success(`Sent to ${data.sent} client librar${data.sent === 1 ? "y" : "ies"}${data.updated ? ` · updated ${data.updated} existing` : ""}`);
            onSent();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send");
        }
        setBusy(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="send-to-clients-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">Send to Client templates</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-[13px] text-[#6F6A64]">
                        Clones {templateIds.length} template{templateIds.length !== 1 ? "s" : ""} into each selected client's own library.
                        They can then customise their copy — later edits to the master won't auto-propagate.
                    </p>
                    <Field label="Completed by">
                        <select data-testid="send-completed-by" value={completedBy} onChange={(e) => setCompletedBy(e.target.value)} className={inputCls}>
                            <option value="client">Client (self-service "Start new report")</option>
                            <option value="pr">PR staff (completed on a visit)</option>
                        </select>
                    </Field>
                    <div>
                        <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6F6A64] mb-1.5">Clients</label>
                        <div className="max-h-56 overflow-y-auto border border-[#E5E0DA] rounded-lg divide-y divide-[#F0EDE8]">
                            {clients.map((c) => (
                                <label key={c.id} data-testid={`send-client-option-${c.id}`}
                                    className="flex items-center gap-2 px-3 py-2 text-[13px] text-[#1F1F1F] cursor-pointer hover:bg-[#F5F3F0]">
                                    <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} />
                                    {c.name}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="send-to-clients-submit" onClick={submit} disabled={busy}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckSquare size={14} />} Send
                    </button>
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]";
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>
            {children}
        </div>
    );
}
